// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import {
  Box,
  Divider,
  Image,
  ModalCloseButton,
  Typography,
  styled,
  useTheme,
} from "@outschool/backpack";
import { MeetingReviewType } from "@outschool/gql-backend-generated";
import { ReviewActivityQuery } from "@outschool/gql-frontend-generated";
import { transformedImageUrl } from "@outschool/media-urls";
import { useMutation } from "@outschool/ui-apollo";
import { SmileyInput } from "@outschool/ui-components-classroom";
import { NudgeModal, TrackedButton } from "@outschool/ui-components-shared";
import { TextArea } from "@outschool/ui-legacy-component-library";
import {
  useDevicePixelRatio,
  useDeviceTypeContext,
  useNavigation,
} from "@outschool/ui-utils";
import React, { useEffect } from "react";
import { useAnalytics } from "use-analytics";

import globeImage from "../../../images/Illustration_Globe_200x200-Color.png";
import { learnerRoutes } from "../lib/Routes";
import { meetingReviewMutation } from "../mutations/MeetingReview";

const CLASS_IMAGE_SIZE = 72;
const TEACHER_IMAGE_SIZE = 32;

const SX_TRUNCATE_TWO_LINES = {
  // truncate text with ellipsis after 2 lines
  display: "-webkit-box",
  overflow: "hidden",
  textOverflow: "ellipsis",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
};

const StyledCloseButton = styled(ModalCloseButton)({
  position: "absolute",
  top: 8,
  right: 8,
  fontSize: "2.4rem",
});

// Once the review is either submitted or dismissed we want to remove the query param.
// This ensures that reloading the page doesn't cause another query and show the modal again.
const clearReviewSectionParam = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete("reviewsection");
  window.history.replaceState({}, "", url.toString());
};

interface ReviewModalProps {
  activity: NonNullable<
    ReviewActivityQuery["activityBySectionUidIfLatestMeetingNotReviewed"]
  >;
  sectionUid: string;
}

const ActivityReviewModal = ({ activity, sectionUid }: ReviewModalProps) => {
  const [open, setOpen] = React.useState(true);

  const [submitted, setSubmitted] = React.useState(false);

  const [submitMeetingReview] = useMutation(meetingReviewMutation);

  const { track } = useAnalytics();

  useEffect(() => {
    track("activity_review_modal_view");
  }, [track]);

  const onClose = (reason: string) => {
    if (reason !== "backdropClick") {
      clearReviewSectionParam();
      setOpen(false);
    }
  };

  return (
    <NudgeModal open={open} onClose={(event, reason) => onClose(reason)}>
      <StyledCloseButton onClick={() => onClose("click")} aria-label="close" />
      {submitted ? (
        <ThanksForFeedback setOpen={setOpen} />
      ) : (
        <FeedbackInput
          activity={activity}
          sectionUid={sectionUid}
          setSubmitted={setSubmitted}
          submitMeetingReview={submitMeetingReview}
        />
      )}
    </NudgeModal>
  );
};

const FeedbackInput = ({
  activity,
  sectionUid,
  setSubmitted,
  submitMeetingReview,
}: {
  activity: NonNullable<
    ReviewActivityQuery["activityBySectionUidIfLatestMeetingNotReviewed"]
  >;
  sectionUid: string;
  setSubmitted: (submitted: boolean) => void;
  submitMeetingReview: any;
}) => {
  const [otherFeedback, setOtherFeedback] = React.useState("");
  const [rating, setRating] = React.useState<number>(0);

  const theme = useTheme();
  const { deviceType } = useDeviceTypeContext();
  const { track } = useAnalytics();

  const pixelRatio = useDevicePixelRatio(deviceType);

  const onSubmit = () => {
    try {
      submitMeetingReview({
        variables: { sectionUid, rating, type: MeetingReviewType.Post },
      });
      if (otherFeedback) {
        track(
          "activity_review_modal_other_feedback",
          {
            activity_uid: activity.uid,
            what_do_you_want_to_learn_next: otherFeedback,
          },
          {
            hideLearnerUid: true,
          },
        );
      }
    } finally {
      clearReviewSectionParam();
      setSubmitted(true);
    }
  };

  const classPhotoUrl = transformedImageUrl(activity.photo?.fileUrl, {
    height: CLASS_IMAGE_SIZE * pixelRatio,
    width: CLASS_IMAGE_SIZE * pixelRatio,
    fit: "crop",
  });
  const teacherPhotoUrl = transformedImageUrl(activity.leader.photo, {
    height: TEACHER_IMAGE_SIZE * pixelRatio,
    width: TEACHER_IMAGE_SIZE * pixelRatio,
    fit: "crop",
  });

  return (
    <>
      <Typography variant="h5" emphasized>
        How did your last class go?
      </Typography>
      <Divider sx={{ my: 12 }} />
      <Box flex sx={{ gap: 16 }}>
        <Image
          sx={{
            width: CLASS_IMAGE_SIZE,
            height: CLASS_IMAGE_SIZE,
            borderRadius: 8,
            objectFit: "cover",
          }}
          src={classPhotoUrl}
          height={CLASS_IMAGE_SIZE}
          width={CLASS_IMAGE_SIZE}
          alt="Class listing photo"
        />
        <Box>
          <Typography
            component="div"
            sx={{
              fontWeight: "500",
              mb: "0.5rem",
              ...SX_TRUNCATE_TWO_LINES,
            }}
          >
            {activity.titleTranslated}
          </Typography>
          <Box flex sx={{ alignItems: "center", gap: 12 }}>
            <Image
              sx={{
                width: TEACHER_IMAGE_SIZE,
                height: TEACHER_IMAGE_SIZE,
                borderRadius: 99, // round
                objectFit: "cover",
              }}
              src={teacherPhotoUrl}
              height={TEACHER_IMAGE_SIZE}
              width={TEACHER_IMAGE_SIZE}
              alt="Teacher photo"
            />
            <Typography
              sx={{
                fontWeight: "500",
                color: "grey.600",
                lineHeight: "1.2",
                ...SX_TRUNCATE_TWO_LINES,
              }}
            >
              {activity.leader.name}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Divider sx={{ my: 12 }} />
      <Box flex sx={{ justifyContent: "center", mb: 8 }}>
        <SmileyInput rating={rating} setRating={setRating} />
      </Box>
      <Box sx={{ margin: "1rem 0" }}>
        <TextArea
          id="other-feedback-textarea"
          sx={{
            borderColor: theme.palette.neutral[300],
          }}
          rows={2}
          maxLength={250}
          showCount
          placeholder="What do you want to learn next?"
          value={otherFeedback}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
            setOtherFeedback(e.target.value);
          }}
        />
      </Box>
      <Box flex sx={{ justifyContent: "center", mb: 16 }}>
        <TrackedButton
          variant="contained"
          fullWidth
          onClick={onSubmit}
          sx={{ px: "6rem" }}
          trackingName="activity_review_submit_review"
          disabled={rating < 1 || rating > 5}
        >
          Submit my rating
        </TrackedButton>
      </Box>
    </>
  );
};

const ThanksForFeedback = ({
  setOpen,
}: {
  setOpen: (open: boolean) => void;
}) => {
  const theme = useTheme();
  const navigate = useNavigation();

  return (
    <Box
      flex
      sx={{
        justifyContent: "center",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        height: "100%",
      }}
    >
      <img src={globeImage} height="120" width="120" alt="Globe" />
      <Typography variant="h5" emphasized>
        Thanks for your feedback!
      </Typography>
      <Typography sx={{ color: theme.palette.grey[600] }}>
        Your feedback helps make Outschool classes even better!
      </Typography>
      <Typography sx={{ color: theme.palette.grey[600] }}>
        Now lets explore other classes!
      </Typography>
      <Box flex sx={{ justifyContent: "center", mt: "4em" }}>
        <TrackedButton
          variant="contained"
          onClick={() => {
            navigate(learnerRoutes.keywordSearchResultsPath(""));
            setOpen(false);
          }}
          sx={{ px: "6rem" }}
          trackingName="activity_review_close_window"
        >
          Continue
        </TrackedButton>
      </Box>
    </Box>
  );
};

export default ActivityReviewModal;
